import { Injectable, OnDestroy } from "@angular/core";
import { ApiService } from "./api.service";
import { BehaviorSubject, Subscription } from "rxjs";
import { LocalStorageService } from "./local-storage.service";
import { Router } from "@angular/router";
import { NotificationAlertService } from "./notification.service";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { DataTransferService } from "./data-transfer.service";
import { LOCAL_STORAGE_KEYS } from "../global/constants";
import { AngularFireMessaging } from "@angular/fire/compat/messaging";

@Injectable({
  providedIn: "root",
})
export class FirebaseService implements OnDestroy {
  currentMessage = new BehaviorSubject(null);
  private subscriptions: Subscription[] = [];
  private serviceWorkerListener: any;

  constructor(
    private angularFireMessaging: AngularFireMessaging,
    private localStorage: LocalStorageService,
    private router: Router,
    private api: ApiService,
    private loader: NgxUiLoaderService,
    private dataService: DataTransferService,
    private readonly notificationService: NotificationAlertService,
  ) {
    this.initialize();
  }

  initialize(): void {
    this.handleNotification();
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to prevent memory leaks
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    // Remove service worker event listener if it exists
    if (this.serviceWorkerListener) {
      navigator.serviceWorker.removeEventListener(
        "message",
        this.serviceWorkerListener,
      );
    }

    // Clean up the behavior subject
    this.currentMessage.complete();
  }

  requestPermission(): void {
    const subscription = this.angularFireMessaging.requestToken.subscribe(
      async (token) => {
        if (token) {
          this.localStorage.setDataInIndexedDB(
            LOCAL_STORAGE_KEYS.FCM_TOKEN,
            token,
          );
          await this.sendTokenToServer(token);
        }
      },
      (err) => {},
    );

    this.subscriptions.push(subscription);
  }

  async sendTokenToServer(token: string): Promise<void> {
    const userId = await this.localStorage.getDataFromIndexedDB(
      LOCAL_STORAGE_KEYS.ID,
    );

    if (userId) {
      const tokenData = {
        deviceType: 2,
        token: token,
      };

      this.api
        .post("notification/fcm", tokenData, true)
        .then((response) => {})
        .catch((error) => {
          console.error("Failed to register FCM token with backend:", error);
        });
    } else {
    }
  }

  navigateToChatUI(conversationId: any, isTicketChat = false) {
    this.router.navigate(["/notification/chat-detail"], {
      state: { conversationId, isTicketChat },
    });
  }

  handleNotification() {
    // Add foreground message handling
    const messagingSubscription = this.angularFireMessaging.messages.subscribe(
      (message: any) => {
        console.log("Foreground message received:", message);
        if (message?.notification) {
          this.notificationService.showSuccess(
            message.notification.body,
            message.notification.title,
          );
        } else if (message?.data) {
          this.notificationService.showSuccess(
            message.data.body,
            message.data.title,
          );
        }
      },
    );
    this.subscriptions.push(messagingSubscription);

    // Existing service worker listener for background notifications
    this.serviceWorkerListener = (event) => {
      console.log("Received firebase message event", event);
      if (event.data.data.title) {
        const notifyData = event.data.data;
        this.notificationService.showSuccess(notifyData.body, notifyData.title);
      }
    };

    navigator.serviceWorker.addEventListener(
      "message",
      this.serviceWorkerListener,
    );
  }
}
